import { useEffect, useRef, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { z } from "zod";
import { isAppForwardUrl, type PinLookup } from "@rago/web-shared";
import { api } from "../../lib/apiClient";
import { isSearchFlag } from "../../lib/appMode";
import { ApiProblemError, auth } from "../../lib/authManager";
import { LinkResultCard, PopupFrame } from "../../components/approval-card";
import { ConsentCard } from "../../components/link-consent";
import { CheckIcon, ClockIcon, XIcon } from "../../components/icons";
import { LoadingState } from "../../components/page-state";
import { ErrorNote, Field, Spinner, SuccessNote } from "../../components/ui";

/**
 * /link — the approver side of a device-link pin (§5.2). Two callers:
 * a TV/CLI showing a 6-char code the user types here, and the server-hosted
 * web app's redirect flow (`?code=`, a 12-char code): that page sent the
 * browser here, we look the pin up at once, show Plex's consent card naming
 * the address (components/link-consent), and after approval either close
 * this window (it was opened as a popup by that page, `?popup=1` — the
 * setup wizard's claim recipe) or, when that page sent its own tab here
 * (`?redirect=1`: popup blocked, or it would have been a full tab — phones,
 * Safari in tabs mode), send the browser back to the return URL the broker
 * stored on the pin — never one from the query string. Both get the bare
 * frame (no app bar, routes/_authed). Cancel never touches the pin: the
 * popup just closes (that page can reopen it); the tab goes back with
 * `?cancelled=1` and that page drops the request. A native app's pin
 * (forwardUrl `rago://auth/link`) is the redirect flow inside the system's
 * sign-in sheet on the user's own device: Approve and Cancel both send the
 * browser to that scheme, which closes the sheet, and the app's own poll
 * collects the session. A server's sign-in is for
 * its owner and the people it is shared with: for any other account the
 * broker says so at the lookup and the card refuses instead of asking — the
 * pin stays pending, so another account can still approve it.
 */
export const Route = createFileRoute("/_authed/link")({
  // The router JSON-parses search values: `?popup=1` arrives as the number 1.
  validateSearch: z.object({
    code: z.string().optional(),
    popup: z.union([z.string(), z.number(), z.boolean()]).optional(),
    redirect: z.union([z.string(), z.number(), z.boolean()]).optional(),
  }),
  component: LinkDevice,
});

const REDIRECT_DELAY_MS = 800;
const CLOSE_DELAY_MS = 1500;

/** The pin problems in the page's own words. */
function describePinError(err: unknown, redirectFlow: boolean): string {
  if (!(err instanceof ApiProblemError)) {
    return "Can't reach Rago right now. Check your connection and try again.";
  }
  const p = err.problem;
  if (p.status === 429 || (p.code as string) === "rate_limited") {
    return "Too many attempts. Wait a few minutes and try again.";
  }
  if (p.code === "pin_expired") {
    return redirectFlow
      ? "This sign-in request has expired. Start again from your server's page."
      : "That code has expired or was already used. Start again on your device.";
  }
  if (p.code === "pin_origin_mismatch") {
    return "This request came from a different network than the one you're on now. Go back to your server's page and start the sign-in again.";
  }
  if (p.code === "server_access_denied") {
    return "This account can't sign in to this server. Only the server's owner and the people it is shared with can sign in here.";
  }
  return p.detail ?? p.title;
}

function isPinExpired(err: unknown): boolean {
  return err instanceof ApiProblemError && err.problem.code === "pin_expired";
}

function isAccessDenied(err: unknown): boolean {
  return err instanceof ApiProblemError && err.problem.code === "server_access_denied";
}

/** "192.168.2.97" from the pin's return URL — where the tab goes back to. */
function forwardHost(forwardUrl: string): string {
  try {
    return new URL(forwardUrl).hostname;
  } catch {
    return forwardUrl;
  }
}

/** Where the browser is going after approval, in the result card's words. */
function returningTo(forwardUrl: string): string {
  return isAppForwardUrl(forwardUrl)
    ? "Returning to the app…"
    : `Returning to ${forwardHost(forwardUrl)}…`;
}

function LinkDevice() {
  const search = Route.useSearch();
  const navigate = useNavigate();
  const [code, setCode] = useState(search.code?.toUpperCase() ?? "");
  const [pin, setPin] = useState<PinLookup | null>(null);
  const [state, setState] = useState<"enter" | "confirm" | "done" | "redirecting">("enter");
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  // A `?code=` is looked up on mount: the spinner, not the code form, until
  // the broker answers.
  const [looking, setLooking] = useState(Boolean(search.code));
  const autoLooked = useRef(false);
  // Opened by the server-hosted app as a popup: approval ends by closing
  // this window; that page is polling the pin and never left.
  const popupMode = isSearchFlag(search.popup);
  // Sent over in the server page's own tab (where a popup would have been
  // a full tab anyway): the same bare page, and every way out goes back.
  const redirectMode = !popupMode && isSearchFlag(search.redirect);

  async function lookup(raw: string) {
    setBusy(true);
    setError(null);
    try {
      const found = await api<PinLookup>(
        `/v1/pins/lookup?code=${encodeURIComponent(raw.trim().toUpperCase())}`,
      );
      setPin(found);
      setState("confirm");
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  // The redirect flow lands here with the code in the URL: look it up at
  // once instead of asking the user to press "Find device".
  useEffect(() => {
    if (autoLooked.current || !search.code) return;
    autoLooked.current = true;
    void lookup(search.code).finally(() => setLooking(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Popup nicety (claim_.approve's idiom): close after the user has seen
  // the confirmation; the opener notices the approval on its next poll.
  useEffect(() => {
    if (!(popupMode && state === "done" && pin?.forwardUrl)) return;
    const t = window.setTimeout(() => window.close(), CLOSE_DELAY_MS);
    return () => window.clearTimeout(t);
  }, [popupMode, state, pin]);

  async function approve() {
    if (!pin) return;
    setBusy(true);
    setError(null);
    try {
      await api(`/v1/pins/${pin.id}/approve`, { method: "POST", body: {} });
      if (pin.forwardUrl && !popupMode) {
        const target = pin.forwardUrl;
        setState("redirecting");
        window.setTimeout(() => window.location.assign(target), REDIRECT_DELAY_MS);
      } else {
        setState("done");
      }
    } catch (err) {
      // Expired while the card was up: the expired state, not a note on a
      // card that can no longer be approved.
      if (isPinExpired(err)) setPin(null);
      // Access lost while the card was up: the bare frame's refusal card,
      // which needs no note.
      if (isAccessDenied(err) && (popupMode || redirectMode)) {
        setPin({ ...pin, serverAccess: false });
        return;
      }
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  /** Close the popup; the page that opened it keeps waiting and can reopen
   * it. Sent over by redirect: back to where the tab came from. Otherwise
   * (close() is a no-op outside a script-opened window): go home. */
  function leave() {
    if (redirectMode) {
      window.history.back();
      return;
    }
    if (popupMode) window.close();
    void navigate({ to: "/" });
  }

  function cancel() {
    // Nothing to revoke: the pin stays pending on the broker until it
    // expires. A tab sent over from the server page goes back there — to the
    // pin's stored return URL, never one from the query string — and that
    // page drops the request (`cancelled=1`, components/local-signin).
    if (pin?.forwardUrl && !popupMode) {
      const back = new URL(pin.forwardUrl);
      back.searchParams.set("cancelled", "1");
      window.location.assign(back.toString());
      return;
    }
    if (pin?.forwardUrl || popupMode) {
      leave();
      return;
    }
    setPin(null);
    setState("enter");
  }

  const redirectFlow = Boolean(pin?.forwardUrl);
  const errorNote = error ? (
    <ErrorNote error={error}>{describePinError(error, redirectFlow || Boolean(search.code))}</ErrorNote>
  ) : null;

  // The popup window: one card per state.
  function popupCard() {
    if (pin?.forwardUrl) {
      if (state === "done" || state === "redirecting") {
        const appPin = isAppForwardUrl(pin.forwardUrl);
        return (
          <LinkResultCard
            tone="ok"
            icon={<CheckIcon size={26} bold />}
            title={appPin ? `${pin.product} is signed in` : "Rago Web is signed in"}
          >
            {state === "redirecting"
              ? returningTo(pin.forwardUrl)
              : "You can close this window. The session appears under Security in your account settings."}
          </LinkResultCard>
        );
      }
      if (pin.serverAccess === false) {
        return (
          <LinkResultCard
            tone="muted"
            icon={<XIcon size={24} />}
            title="This account can't sign in to this server"
            actions={
              <>
                {/* The account chip's own way out: sign-in, and `next`
                    brings the window back to this same request. */}
                <button
                  type="button"
                  className="rago-btn rago-btn-lg min-w-0 flex-1"
                  onClick={() => void auth.signOut()}
                >
                  Use another account
                </button>
                <button
                  type="button"
                  className="rago-btn rago-btn-quiet rago-btn-lg min-w-0 flex-1"
                  onClick={cancel}
                >
                  {redirectMode ? "Go back" : "Close window"}
                </button>
              </>
            }
          >
            Only the server's owner and the people it is shared with can sign in here.
          </LinkResultCard>
        );
      }
      return (
        <ConsentCard
          pin={{ ...pin, forwardUrl: pin.forwardUrl }}
          busy={busy}
          error={errorNote}
          onApprove={() => void approve()}
          onCancel={cancel}
        />
      );
    }
    if (looking || busy) return <LoadingState minH="min-h-[240px]" label="Loading the request" />;
    const closeButton = (
      <button
        type="button"
        className="rago-btn rago-btn-quiet rago-btn-lg min-w-0 flex-1"
        onClick={leave}
      >
        {redirectMode ? "Go back" : "Close window"}
      </button>
    );
    // No code, an expired one, or one already used.
    if (!search.code || !error || isPinExpired(error)) {
      return (
        <LinkResultCard
          tone="muted"
          icon={<ClockIcon size={24} />}
          title="This sign-in request has expired"
          actions={closeButton}
        >
          Start again from your server's page.
        </LinkResultCard>
      );
    }
    // The broker unreachable or rate-limiting: say so, offer another try.
    return (
      <LinkResultCard
        tone="muted"
        icon={<XIcon size={24} />}
        title="Couldn't load this request"
        actions={
          <>
            <button
              type="button"
              className="rago-btn rago-btn-lg min-w-0 flex-1"
              onClick={() => search.code && void lookup(search.code)}
            >
              Try again
            </button>
            {closeButton}
          </>
        }
      >
        {describePinError(error, true)}
      </LinkResultCard>
    );
  }

  if ((popupMode || redirectMode) && !(pin && !pin.forwardUrl)) {
    return <PopupFrame>{popupCard()}</PopupFrame>;
  }

  // An older server build sends its tab here without a flag: the same
  // consent card under the app bar; Approve and Cancel go back to the
  // stored return URL.
  if (pin?.forwardUrl && (state === "confirm" || state === "redirecting" || state === "done")) {
    return (
      <div className="rago-page mx-auto max-w-[540px] px-6 pb-16">
        <div className="rago-rise">
          {state === "confirm" ? (
            <ConsentCard
              pin={{ ...pin, forwardUrl: pin.forwardUrl }}
              busy={busy}
              error={errorNote}
              onApprove={() => void approve()}
              onCancel={cancel}
            />
          ) : (
            <LinkResultCard
              tone="ok"
              icon={<CheckIcon size={26} bold />}
              title="Rago Web is signed in"
            >
              {state === "redirecting" ? returningTo(pin.forwardUrl) : "You can close this window."}
            </LinkResultCard>
          )}
        </div>
      </div>
    );
  }

  if (looking) return <LoadingState />;

  return (
    <div className="rago-page mx-auto max-w-md px-6 pb-16">
      <h1 className="rago-display rago-rise mb-1 text-2xl">Link a device</h1>
      <p className="rago-rise rago-rise-1 mb-8 text-sm text-[var(--rago-text-2)]">
        Enter the code shown on your TV or device.
      </p>
      <div className="rago-rise rago-rise-2">
        {errorNote}
        {state === "enter" ? (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              void lookup(code);
            }}
            noValidate
          >
            <Field label="Device code" htmlFor="code">
              <input
                id="code"
                autoFocus
                autoCapitalize="characters"
                autoComplete="off"
                spellCheck={false}
                maxLength={12}
                className="rago-input text-center font-mono text-2xl tracking-[0.5em] uppercase"
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
              />
            </Field>
            <button className="rago-btn rago-btn-block mt-2" disabled={busy || code.trim().length < 6}>
              {busy ? <Spinner /> : null}
              Find device
            </button>
          </form>
        ) : state === "confirm" && pin ? (
          <div className="rounded-[var(--rago-radius-l)] border border-[var(--rago-line-1)] bg-[var(--rago-bg-1)] p-6">
            <p className="mb-1 text-sm text-[var(--rago-text-2)]">Approve this device?</p>
            <p className="rago-display mb-1 text-lg">{pin.name}</p>
            <p className="mb-5 text-xs text-[var(--rago-text-3)]">
              {pin.product} · {pin.platform}
            </p>
            <div className="flex gap-3">
              <button className="rago-btn" onClick={() => void approve()} disabled={busy}>
                {busy ? <Spinner /> : null}
                Approve
              </button>
              <button className="rago-btn rago-btn-quiet" onClick={cancel} disabled={busy}>
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <SuccessNote>
            Device linked. It will sign in by itself within a few seconds.
          </SuccessNote>
        )}
      </div>
    </div>
  );
}
